import { WebFormQuestionResponseTableFieldFragment$key } from "@/web-form/table/__generated__/WebFormQuestionResponseTableFieldFragment.graphql"
import { formatValueText, indexToLetter } from "@/web-form/utils/webFormFillerUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoLink, DiscoText, DiscoTooltip } from "@disco-ui"
import classNames from "classnames"
import { graphql, useFragment } from "react-relay"

interface Props {
  webFormQuestionKey: WebFormQuestionResponseTableFieldFragment$key
}

function WebFormQuestionResponseTableField({ webFormQuestionKey }: Props) {
  const classes = useStyles()

  const webFormQuestion = useFragment<WebFormQuestionResponseTableFieldFragment$key>(
    graphql`
      fragment WebFormQuestionResponseTableFieldFragment on WebFormQuestion
      @argumentDefinitions(
        organizationMembershipId: { type: "ID!" }
        contentUsageId: { type: "ID" }
      ) {
        type
        options {
          id
          label
        }
        answer(
          organizationMembershipId: $organizationMembershipId
          contentUsageId: $contentUsageId
        ) {
          isSkipped
          body
          selectedOptions
        }
      }
    `,
    webFormQuestionKey
  )

  return <div>{renderValue()}</div>

  function renderValue() {
    type FullOption = { id: string; label: string; indexFromOptions: number }
    const questionOptionMapById = webFormQuestion.options?.reduce(
      (acc, option, index) => {
        acc[option.id] = { ...option, indexFromOptions: index }
        return acc
      },
      {} as Record<string, FullOption>
    )

    const selectedOptions = webFormQuestion.answer?.selectedOptions
      ?.map((selectedOptionId) => {
        return questionOptionMapById?.[selectedOptionId]
      })
      .filter(Boolean) as FullOption[]

    if (!webFormQuestion.answer) {
      return (
        <DiscoText variant={"body-sm"} color={"text.secondary"} className={"fs-mask"}>
          {"-"}
        </DiscoText>
      )
    }

    const questionType = webFormQuestion?.type

    switch (questionType) {
      case "link": {
        const value = getValueText()
        return (
          <DiscoText
            variant={"body-sm"}
            // Don't open the profile drawer when clicking a link
            onClick={(e) => e.stopPropagation()}
            className={classes.linkContainer}
            truncateText={3}
          >
            <DiscoLink
              to={value}
              target={"_blank"}
              rel={"noreferrer noopener"}
              className={classes.link}
            >
              {value.replace(/.*:\/\//, "")}
            </DiscoLink>
          </DiscoText>
        )
      }
      case "single_select":
      case "multiple_select":
        if (!selectedOptions) return "-"
        return (
          <>
            {selectedOptions.map((option) => {
              return (
                <DiscoTooltip key={option.id} content={option.label}>
                  <div
                    key={option.id}
                    className={classes.singleOrMultiSelectValueContainer}
                  >
                    <span
                      className={classNames(
                        classes.optionIcon,
                        webFormQuestion!.type === "multiple_select" &&
                          classes.multipleSelect
                      )}
                    >
                      {indexToLetter(option.indexFromOptions)}
                    </span>
                    <DiscoText
                      variant={"body-sm"}
                      className={classes.optionText}
                      component={"span"}
                      noWrap
                    >
                      {option.label}
                    </DiscoText>
                  </div>
                </DiscoTooltip>
              )
            })}
          </>
        )
      case "ranking": {
        if (!selectedOptions) return "-"
        const values = getValueText().split("\n")
        return (
          <>
            {values.map((label) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div key={label}>
                  <DiscoText
                    variant={"body-sm"}
                    className={classes.optionText}
                    component={"span"}
                    noWrap
                  >
                    {label}
                  </DiscoText>
                </div>
              )
            })}
          </>
        )
      }
      case "rating": {
        const value = getValueText()
        return (
          <DiscoText
            variant={"body-sm"}
            className={classes.optionText}
            component={"span"}
            noWrap
          >
            {value}
          </DiscoText>
        )
      }
      default: {
        const value = getValueText()
        return (
          <DiscoText
            variant={"body-sm"}
            color={"text.secondary"}
            className={classNames(classes.text, classes.textWrap, "fs-mask")}
            truncateText={3}
          >
            {value || "-"}
          </DiscoText>
        )
      }
    }
  }

  function getValueText() {
    return formatValueText({
      ...webFormQuestion.answer!,
      selectedOptions: webFormQuestion.answer!.selectedOptions as string[],
      webFormQuestion: {
        type: webFormQuestion.type,
        options: webFormQuestion.options as { id: string; label: string }[],
      },
    })
  }
}

const useStyles = makeUseStyles((theme) => ({
  linkContainer: {
    maxWidth: "300px",
    wordWrap: "break-word",
  },
  link: {
    fontSize: "inherit",
    lineHeight: "inherit",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  singleOrMultiSelectValueContainer: {
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.groovy.neutral[200]}`,
    borderRadius: theme.measure.borderRadius.medium,
    display: "flex",
    alignItems: "flex-start",
    backgroundColor: theme.palette.background.paper,
    maxWidth: "350px",
    width: "fit-content",
    "&:not(:first-child)": {
      marginTop: theme.spacing(1),
    },
  },
  optionIcon: {
    height: "20px",
    width: "20px",
    backgroundColor: theme.palette.groovy.neutral[100],
    border: `1px solid ${theme.palette.groovy.neutral[200]}`,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    color: theme.palette.common.black,
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
  },
  optionText: {
    color: `${theme.palette.text.secondary} !important`,
  },
  multipleSelect: {
    borderRadius: "4px",
  },
  text: {
    width: "400px",
  },
  textWrap: {
    whiteSpace: "pre-line",
    overflowWrap: "break-word",
  },
}))

export default WebFormQuestionResponseTableField
